"use client";

import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import type { TabSource } from "../debug-panel.types";
import { getFileLineCount, getLineCountColor } from "../utils/file-lines";

// ── CopyButton ────────────────────────────────────────────────────────────────

function fallbackCopy(text: string, setCopied: (v: boolean) => void) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.cssText = "position:fixed;opacity:0;left:-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  setCopied(true);
  setTimeout(() => setCopied(false), 1500);
}

export function CopyButton({ text }: { text: string }) {
  const t = useTranslations("debug.actions");
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {
          fallbackCopy(text, setCopied);
        });
    } else {
      fallbackCopy(text, setCopied);
    }
  };
  return (
    <button
      onClick={handleCopy}
      className="shrink-0 ml-1 px-1.5 py-0.5 text-sm rounded border border-border bg-muted/50 text-muted-foreground opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-opacity"
    >
      {copied ? t("copiedShort") : t("copy")}
    </button>
  );
}

// ── DebugSection ──────────────────────────────────────────────────────────────

export function DebugSection({
  title,
  accent,
  defaultOpen = true,
  forceOpen,
  actions,
  children,
}: {
  title: string;
  accent: string;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen || open;
  return (
    <div className={cn("border-l-2 rounded-r-lg transition-colors", accent)}>
      <div className="flex items-center justify-between w-full rounded-tr-lg hover:bg-muted/30 transition-colors">
        <button
          onClick={() => setOpen((p) => !p)}
          className="flex items-center gap-2 flex-1 px-4 py-2.5 text-left"
        >
          <svg
            className={cn(
              "w-3.5 h-3.5 text-muted-foreground transition-transform",
              !isOpen && "-rotate-90",
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
          <span className="text-sm font-semibold">{title}</span>
        </button>
        {actions && <div className="px-3">{actions}</div>}
      </div>
      {isOpen && <div className="px-4 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

// ── DebugTableRows ────────────────────────────────────────────────────────────

export function DebugTableRows({
  rows,
}: {
  rows: Array<{
    label: string;
    value?: string;
    mono?: boolean;
    node?: React.ReactNode;
  }>;
}) {
  return (
    <div className="space-y-0">
      {rows.map((row, i) => (
        <div
          key={i}
          className="flex items-center gap-4 py-1.5 text-sm border-b border-border/15 last:border-0 group"
        >
          <span className="text-muted-foreground w-28 shrink-0">
            {row.label}
          </span>
          {row.node || (
            <span
              className={
                row.mono
                  ? "font-mono text-muted-foreground break-all"
                  : "text-foreground"
              }
            >
              {row.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── DebugCodePath ─────────────────────────────────────────────────────────────

export function DebugCodePath({
  path,
  className,
}: {
  path: string;
  className?: string;
}) {
  const t = useTranslations("debug.files");
  const lineCount = getFileLineCount(path);
  return (
    <code className={cn("font-mono text-sm break-all", className)}>
      <span>{path}</span>
      {lineCount !== null && (
        <span
          className={cn(
            "font-semibold ml-0.5 select-all",
            getLineCountColor(lineCount),
          )}
          title={
            lineCount > 500
              ? t("lineCountExceeded", { count: lineCount })
              : t("lineCount", { count: lineCount })
          }
        >
          {":"}
          {lineCount}
        </span>
      )}
    </code>
  );
}

// ── DebugFilePath ─────────────────────────────────────────────────────────────

export function DebugFilePath({
  path,
  label,
}: {
  path: string;
  label?: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm group">
      {label ? (
        <span className="text-muted-foreground w-16 shrink-0 text-sm">
          {label}
        </span>
      ) : (
        <span className="text-muted-foreground/50 shrink-0">{"→"}</span>
      )}
      <DebugCodePath path={path} className="flex-1" />
      <CopyButton text={path} />
    </div>
  );
}

// ── DebugSourceDisplay ────────────────────────────────────────────────────────

export function DebugSourceDisplay({ source }: { source: TabSource }) {
  if (source.kind === "file") return <DebugCodePath path={source.path} />;
  return (
    <span className="text-sm text-purple-600 dark:text-purple-400 font-mono">
      {"<"}
      {source.name}
      {"/>"}
    </span>
  );
}
